"use client";

/**
 * Amanda V2 typed cards (spec §3.1 / T13).
 *
 * The backend no longer tells the widget "I did it" — it hands over a CARD
 * describing something it PREPARED. Every state-changing verb ends here, at a
 * button that navigates the human into the product's own screen, where the
 * product's own authenticated route executes it. So the card's copy must never
 * claim the action happened: it says what was prepared, what still blocks it,
 * and whose workspace it lives in.
 *
 * Two shapes reach this component and they disagree about one field:
 *   live SSE   -> { type: "card", kind, action: <verb>, ... }
 *   history    -> { action: "card", card_action: <verb>, kind, ... }
 * (the persisted entry has to be tagged `action: "card"` so the renderer
 * switch finds it at all, which costs it the verb). `verbOf` reads both.
 */

import { useParams, useRouter } from "next/navigation";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth";

type Blocker = { code: string; message: string; fix_hint?: string };
type CardRecipient = { id: string; name: string; email: string; role?: string; field_count: number };

const PAST_TENSE: Record<string, string> = {
  send: "sent",
  bulk_send: "sent",
  resend: "resent",
  void: "voided",
  delete: "deleted",
};

function verbOf(data: Record<string, unknown>): string {
  const verb = (data.card_action ?? data.action) as string | undefined;
  return verb && verb !== "card" ? verb : "";
}

function pastTense(action: string): string {
  return PAST_TENSE[action] ?? "done";
}

export function AmandaCard({ data }: { data: Record<string, unknown>; isLive?: boolean }) {
  const kind = data.kind as string;
  if (kind === "prepared_action") return <PreparedActionCard data={data} />;
  if (kind === "draft_change") return <DraftChangeCard data={data} />;
  if (kind === "error") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
          {String(data.message ?? "Something went wrong.")}
        </div>
      </div>
    );
  }
  // `navigation` and `fact` carry nothing the user needs as a card — routing
  // still rides the legacy `navigate_to` action, and facts are in the prose.
  return null;
}

function PreparedActionCard({ data }: { data: Record<string, unknown> }) {
  const router = useRouter();
  const params = useParams();
  const { getAccessToken } = useAuth();

  const action = verbOf(data);
  const blockers = (data.blockers as Blocker[]) || [];
  const here = (params?.org as string | undefined) ?? null;
  const slug = (data.org_slug as string | null) ?? null;
  const elsewhere = !!slug && slug !== here;
  const workspaceLabel = (typeof data.workspace_name === "string" && data.workspace_name) || slug;
  const recipientCount = typeof data.recipient_count === "number" ? (data.recipient_count as number) : null;

  // The card's destination is model-adjacent data: it reaches this component
  // through the SSE stream and, on replay, out of a stored JSON blob. Only an
  // in-app absolute path is navigable — `//evil.tld` is protocol-relative and
  // would leave the product entirely — so anything else disables the button
  // rather than being "cleaned up" into something the user didn't ask for.
  const url = data.url;
  const navigable = typeof url === "string" && url.startsWith("/") && !url.startsWith("//");

  const go = () => {
    if (!navigable) return;
    const token = getAccessToken();
    const prepId = data.prep_id as string | undefined;
    // Fire-and-forget telemetry: a failed stamp must never block the human.
    if (prepId && token) {
      apiFetch(`/chat/prepared/${prepId}/opened`, { method: "POST", token }).catch(() => {});
    }
    router.push(url as string);
  };

  return (
    <div className="flex justify-start" data-amanda-card="prepared_action">
      <div className="max-w-[85%] w-full rounded-2xl border border-[var(--color-gold)]/40 bg-[var(--color-gold)]/5 px-3.5 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-[var(--color-ink)]">{String(data.title ?? "Prepared action")}</span>
          {data.is_specimen ? (
            <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-white">SPECIMEN</span>
          ) : null}
          {recipientCount !== null ? (
            <span className="text-[10px] text-slate-500">
              {recipientCount} recipient{recipientCount === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>

        <p className="text-xs leading-relaxed text-slate-600">
          {"Prepared for your review"}
          {data.on_behalf_of ? ` on behalf of ${String(data.on_behalf_of)}` : ""}
          {elsewhere ? ` — in workspace ${workspaceLabel}` : ""}
          {". Nothing was "}
          {pastTense(action)}
          {" yet — you complete this in SignForge."}
        </p>

        {blockers.length > 0 && (
          <ul className="mt-2 space-y-1 text-[11px] leading-relaxed text-amber-800">
            {blockers.map((b, i) => (
              <li key={b.code || i}>
                {"• "}
                {b.message}
                {b.fix_hint ? ` — ${b.fix_hint}` : ""}
              </li>
            ))}
          </ul>
        )}

        <button
          onClick={go}
          disabled={!navigable}
          title={navigable ? undefined : "This link can't be opened here. Ask Amanda to prepare it again."}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-gold)] px-3.5 py-2 text-xs font-semibold text-[var(--color-ink)] shadow-sm transition-colors hover:bg-[var(--color-gold-light)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[var(--color-gold)]"
        >
          {String(data.button_label ?? "Open in SignForge")}
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function DraftChangeCard({ data }: { data: Record<string, unknown> }) {
  const recipients = (data.recipients as CardRecipient[]) || [];
  return (
    <div className="flex justify-start" data-amanda-card="draft_change">
      <div className="max-w-[85%] w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-xs shadow-sm">
        <div className="font-medium text-slate-800">{String(data.what ?? "Draft updated.")}</div>
        {recipients.length > 0 && (
          <table className="mt-2 w-full text-left">
            <tbody>
              {recipients.map((r, i) => (
                <tr key={r.id || i} className="border-t border-slate-100">
                  <td className="py-1 pr-2 text-slate-800">{r.name}</td>
                  <td className="py-1 pr-2 text-slate-500">{r.email}</td>
                  <td className="py-1 text-right text-slate-500 whitespace-nowrap">{r.field_count} fields</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="mt-1.5 text-[11px] text-slate-500">
          {data.has_document ? "PDF attached" : "No PDF yet"} · {String(data.field_count ?? 0)} fields total
        </div>
        {typeof data.document_note === "string" && data.document_note && (
          <div role="note" data-amanda-note="document" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11.5px] leading-snug text-amber-900">
            {data.document_note}
          </div>
        )}
      </div>
    </div>
  );
}
